import React, { useState, useEffect } from "react";
import {
  getSubcategories,
  setSubcategories,
  deleteSubcategory
} from "../../functions/Categories";

const SECTION_LABELS = {
  india: "India",
  international: "International",
  weekend: "Weekend Trips",
  more: "More",
};

const Category = () => {
  const [activeSection, setActiveSection] = useState("india");
  const [subcategories, setSubcategoriesState] = useState([""]);
  const [editIndex, setEditIndex] = useState(null);
  const [subcatInput, setSubcatInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch subcategories from Firestore on section change
  useEffect(() => {
    const fetchSubcategories = async () => {
      setLoading(true);
      try {
        const data = await getSubcategories(activeSection);
        setSubcategoriesState(data);
      } catch (e) {
        setSubcategoriesState([]);
      }
      setEditIndex(null);
      setSubcatInput("");
      setLoading(false);
    };
    fetchSubcategories();
  }, [activeSection]);

  // Save subcategories to Firestore
  const saveSubcategoriesToFirestore = async (newSubcategories) => {
    setLoading(true);
    await setSubcategories(activeSection, newSubcategories);
    setLoading(false);
  };

  const handleAddSubcategory = async () => {
    if (!subcatInput.trim()) return;
    const newSubcategories = [...subcategories, subcatInput.trim()];
    setSubcategoriesState(newSubcategories);
    setSubcatInput("");
    setEditIndex(null);
    await saveSubcategoriesToFirestore(newSubcategories);
  };

  const handleRemoveSubcategory = async (idx) => {
    if (!window.confirm("Are you sure you want to delete this subcategory?")) return;
    const subToDelete = subcategories[idx];
    setLoading(true);
    const updated = await deleteSubcategory(activeSection, subToDelete);
    setSubcategoriesState(updated);
    setEditIndex(null);
    setSubcatInput("");
    setLoading(false);
  };

  const handleEdit = (idx) => {
    setEditIndex(idx);
    setSubcatInput(subcategories[idx]);
  };

  const handleUpdate = async () => {
    if (!subcatInput.trim()) return;
    const newSubcategories = subcategories.map((s, i) => (i === editIndex ? subcatInput.trim() : s));
    setSubcategoriesState(newSubcategories);
    setEditIndex(null);
    setSubcatInput("");
    await saveSubcategoriesToFirestore(newSubcategories);
  };

  const handleCancel = () => {
    setEditIndex(null);
    setSubcatInput("");
  };

  return (
    <div className="category-admin">
      <div className="mb-4">
        <div className="btn-group" role="group" aria-label="Category Sections">
          {Object.keys(SECTION_LABELS).map((section) => (
            <button
              key={section}
              className={`btn btn-outline-secondary${activeSection === section ? " active" : ""}`}
              onClick={() => setActiveSection(section)}
            >
              {SECTION_LABELS[section]}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div>
          <h4>Manage Subcategories in {SECTION_LABELS[activeSection]}</h4>
          <div className="admin-card">
            <div className="card-header">
              <h3>Manage Subcategories</h3>
            </div>
            <div className="card-body">
              <form
                onSubmit={e => {
                  e.preventDefault();
                  editIndex !== null ? handleUpdate() : handleAddSubcategory();
                }}
              >
                <div className="mb-3">
                  <label className="form-label">
                    {editIndex !== null ? "Edit Subcategory" : "Add Subcategory"}
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={subcatInput}
                      onChange={e => setSubcatInput(e.target.value)}
                      placeholder="Subcategory name"
                      required
                    />
                    <button
                      type="submit"
                      className={`btn btn-${editIndex !== null ? "success" : "primary"}`}
                      disabled={loading}
                    >
                      {editIndex !== null ? "Update" : "Add"}
                    </button>
                    {editIndex !== null && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </form>
              <hr className="my-4" />
              <h5>Existing Subcategories</h5>
              {loading ? (
                <div className="alert alert-info">Loading...</div>
              ) : (!subcategories || subcategories.length === 0) ? (
                <div className="alert alert-info">No subcategories added yet.</div>
              ) : (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>Subcategory</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subcategories
                      .filter(s => s && s.trim())
                      .map((sub, idx) => (
                        <tr key={idx}>
                          <td>{sub}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(idx)}
                              disabled={loading}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveSubcategory(idx)}
                              disabled={loading}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Category;
